import { Navigate, Route, Routes } from 'react-router-dom';

import { Box } from '@mui/material';
import { CreateCustomer } from 'customers/CreateCustomer';
import { CustomerDetails } from 'customers/CustomerDetails';
import { CustomerList } from 'customers/CustomerList';
import { EditCustomerWrapper } from 'customers/EditCustomerWrapper';
import { RevertCustomer } from 'customers/RevertCustomer';
import { Dashboard } from 'dashboard/Dashboard';
import { ConfigurationDetails } from 'productConfigurations/ConfigurationDetails';
import { ConfigurationList } from 'productConfigurations/ConfigurationList';
import { CreateConfiguration } from 'productConfigurations/CreateConfiguration';
import { EditConfiguration } from 'productConfigurations/EditConfiguration';
import { ProfilePage } from 'profile/ProfilePage';

import { ClaimLicense } from 'commonComponents/ClaimLicense';
import { LoadingIndicator } from 'commonComponents/LoadingIndicator';

import { useUser } from 'api/authorization';
import { useCurrentProduct } from 'api/products';
import { useSettings } from 'api/settings';
import { AppBar } from 'appBar/AppBar';
import { CreateLicense } from 'licenses/CreateLicense';
import { EditLicense } from 'licenses/EditLicense';
import { LicenseBilling } from 'licenses/LicenseBilling';
import { LicenseDetails } from 'licenses/LicenseDetails/LicenseDetails';
import { LicenseFileDetails } from 'licenses/LicenseFiles/LicenseFileDetails';
import { CreateLicenseFile } from 'licenses/LicenseFiles/LicenseFileForm/CreateLicenseFile';
import { EditLicenseFile } from 'licenses/LicenseFiles/LicenseFileForm/EditLicenseFile';
import { LicenseList } from 'licenses/LicenseList/LicenseList';
import { UpgradeLicense } from 'licenses/UpgradeLicense';
import { LoginPage } from 'loginPage/LoginPage';
import { ResetPasswordConfirmPage } from 'resetPassword/ResetPasswordConfirmPage';
import { ResetPasswordPage } from 'resetPassword/ResetPasswordPage';
import { ROLE_DEFINITIONS } from 'roles/definitions';
import { NotAllowedPage } from 'routes/NotAllowedPage';
import { NotFoundPage } from 'routes/NotFoundPage';
import { RequireAuth, RequireRole, RequireValidUser } from 'routes/PrivateRoute';
import { ROUTES, useDefaultRoute } from 'routes/routes';
import { CreateUser } from 'users/CreateUser';
import { EditUser } from 'users/EditUser';
import { EmailUser } from 'users/EmailUser';
import { UserConfirmation } from 'users/UserConfirmation';
import { UserDetails } from 'users/UserDetails';
import { UserList } from 'users/UserList';
import { UserRegistration } from 'users/UserRegistration';
import { UserRegistrationIncomplete } from 'users/UserRegistrationIncomplete';

function RedirectToDefaultRoute() {
    const defaultRoute = useDefaultRoute();
    return <Navigate to={defaultRoute} replace />;
}

export function App() {
    const globalDataLoaded = useLoadGlobalData();

    if (!globalDataLoaded) {
        return <LoadingIndicator />;
    }

    return (
        <Box
            data-testid="App"
            sx={{
                position: 'fixed',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
            }}
        >
            <AppBar />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    flexGrow: 1,
                }}
            >
                <Routes>
                    <Route path={ROUTES.LOGIN} element={<LoginPage />} />
                    <Route
                        path={ROUTES.DASHBOARD}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.LicenseManager}>
                                <Dashboard />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.LICENSE_CREATE_WITH_CUSTOMER}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.PrivilegedCustomer}>
                                <CreateLicense />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.LICENSE_CLAIM}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.Customer}>
                                <RequireValidUser>
                                    <ClaimLicense />
                                </RequireValidUser>
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.LICENSE_LIST}
                        element={
                            <RequireAuth>
                                <LicenseList />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={ROUTES.LICENSE_DETAIL}
                        element={
                            <RequireAuth>
                                <LicenseDetails />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={ROUTES.LICENSEFILE_DETAIL}
                        element={
                            <RequireAuth>
                                <LicenseFileDetails />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={ROUTES.LICENSEFILE_CREATE}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.Customer}>
                                <CreateLicenseFile />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.LICENSEFILE_EDIT}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.LicenseManager}>
                                <EditLicenseFile />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.LICENSE_BILLING}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.LicenseManager}>
                                <LicenseBilling />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.LICENSE_EDIT}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.PrivilegedCustomer}>
                                <EditLicense />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.LICENSE_UPGRADE}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.PrivilegedCustomer}>
                                <UpgradeLicense />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.CUSTOMER_LIST}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.Customer}>
                                <CustomerList />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.CUSTOMER_CREATE}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.PrivilegedCustomer}>
                                <CreateCustomer />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.CUSTOMER_DETAIL}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.Customer}>
                                <CustomerDetails />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.CUSTOMER_EDIT}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.PrivilegedCustomer}>
                                <EditCustomerWrapper />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.CUSTOMER_REVERT}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.LicenseManager}>
                                <RevertCustomer />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.USER_CREATE}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.Administrator}>
                                <CreateUser />
                            </RequireRole>
                        }
                    />
                    <Route path={ROUTES.USER_REGISTER} element={<UserRegistration />} />
                    <Route path={ROUTES.USER_REGISTER_INCOMPLETE} element={<UserRegistrationIncomplete />} />
                    <Route path={ROUTES.USER_CONFIRM} element={<UserConfirmation />} />
                    <Route
                        path={ROUTES.USER_EMAIL}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.LicenseManager}>
                                <EmailUser />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.USER_LIST}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.Administrator}>
                                <UserList />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.USER_DETAIL}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.Administrator}>
                                <UserDetails />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.USER_EDIT}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.Administrator}>
                                <EditUser />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.CONFIGURATION_LIST}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.PrivilegedCustomer}>
                                <ConfigurationList />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.CONFIGURATION_CREATE}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.LicenseManager}>
                                <CreateConfiguration />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.CONFIGURATION_DETAIL}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.PrivilegedCustomer}>
                                <ConfigurationDetails />
                            </RequireRole>
                        }
                    />
                    <Route
                        path={ROUTES.CONFIGURATION_EDIT}
                        element={
                            <RequireRole role={ROLE_DEFINITIONS.LicenseManager}>
                                <EditConfiguration />
                            </RequireRole>
                        }
                    />
                    <Route path={ROUTES.RESET_PASSWORD_REQUEST} element={<ResetPasswordPage />} />
                    <Route path={ROUTES.RESET_PASSWORD_CONFIRM} element={<ResetPasswordConfirmPage />} />
                    <Route
                        path={ROUTES.PROFILE}
                        element={
                            <RequireAuth>
                                <ProfilePage />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={ROUTES.NOT_ALLOWED}
                        element={
                            <RequireAuth>
                                <NotAllowedPage />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path={'/'}
                        element={
                            <RequireAuth>
                                <RedirectToDefaultRoute />
                            </RequireAuth>
                        }
                    />
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </Box>
        </Box>
    );
}

function useLoadGlobalData() {
    const { isLoading: isLoadingUser, data: user } = useUser();
    const { isLoading: isLoadingSettings } = useSettings();
    const { isLoading: isLoadingCurrentProduct } = useCurrentProduct();

    return !isLoadingUser && !isLoadingSettings && (!isLoadingCurrentProduct || !user);
}
